import { useEffect, useId, useRef, useState, type ChangeEvent } from "react";
import { cx } from "./tokens.ts";

export type CodeCellsProps = {
  /** The digits typed so far (0 to `length`). */
  value: string;
  onChange: (value: string) => void;
  /** The last digit was entered (typed, pasted or autofilled): submit. */
  onComplete?: (value: string) => void;
  length?: number;
  /** The field's name for screen readers ("Authentication code"). */
  label: string;
  /** Each cell's label: "Digit {n} of 6". */
  cellLabel: (n: number) => string;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  testID: string;
};

/**
 * A one-time code as six cells (3 + 3 with a dash, as Slack does) over ONE real `one-time-code` input, so paste, SMS /
 * password-manager autofill and screen readers keep working: typing fills the next cell, Backspace goes back, and the
 * last digit calls `onComplete`. Native: one TextField with `.oneTimeCode` content type drawn as cells.
 */
export function CodeCells({ value, onChange, onComplete, length = 6, label, cellLabel, error, disabled, autoFocus, testID }: CodeCellsProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (autoFocus === true) input.current?.focus();
  }, [autoFocus]);
  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    let digits = e.target.value.replace(/\D/g, "");
    // Typing (or pasting) into a full field starts over with what was just entered.
    if (value.length === length && digits.length > length) digits = digits.slice(length);
    digits = digits.slice(0, length);
    onChange(digits);
    if (digits.length === length && digits !== value) onComplete?.(digits);
  };
  const half = Math.ceil(length / 2);
  const active = Math.min(value.length, length - 1);
  return (
    <div className={cx("b-code", error === true && "b-code-error", disabled === true && "b-code-disabled")} onClick={() => input.current?.focus()}>
      <label className="b-visually-hidden" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        ref={input}
        className="b-code-input"
        value={value}
        onChange={onInput}
        onFocus={(e) => {
          setFocused(true);
          // After a wrong code, typing starts over (the old digits are selected).
          e.currentTarget.select();
        }}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        // Read-only, not disabled, while verifying: a disabled input loses focus and the next try needs a click.
        readOnly={disabled}
        aria-disabled={disabled === true ? true : undefined}
        aria-invalid={error === true ? true : undefined}
        data-testid={testID}
      />
      {Array.from({ length }, (_, i) => (
        <CellAt key={i} i={i} half={half} digit={value[i]} active={focused && i === active} label={cellLabel(i + 1)} testID={`${testID}-cell-${i + 1}`} />
      ))}
    </div>
  );
}

function CellAt({ i, half, digit, active, label, testID }: { i: number; half: number; digit: string | undefined; active: boolean; label: string; testID: string }) {
  return (
    <>
      {i === half ? <span className="b-code-dash" aria-hidden="true" /> : null}
      <span className={cx("b-code-cell", active && "b-code-cell-active")} aria-label={label} aria-hidden="true" data-testid={testID}>
        {digit ?? ""}
      </span>
    </>
  );
}
